'use client'

import { ConfirmationModal } from '.'
import * as React from 'react'

export interface ConfirmationModalOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void | Promise<void>
}

export interface ConfirmationModalContextModel {
  isOpen: boolean
  openModal: (options: ConfirmationModalOptions) => void
  closeModal: () => void
  title: string
  message: string
  confirmText: string
  cancelText: string
  isLoading: boolean
  onConfirm?: () => void | Promise<void>
}

const ConfirmationModalContext = React.createContext<ConfirmationModalContextModel>(null!)

export const ConfirmationModalProvider = ({ children }: React.PropsWithChildren) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [title, setTitle] = React.useState<string>('')
  const [message, setMessage] = React.useState<string>('')
  const [confirmText, setConfirmText] = React.useState<string>('Confirm')
  const [cancelText, setCancelText] = React.useState<string>('Cancel')
  const [onConfirmCallback, setOnConfirmCallback] = React.useState<
    (() => void | Promise<void>) | undefined
  >()

  const openModal = (options: ConfirmationModalOptions) => {
    setTitle(options.title)
    setMessage(options.message)
    setConfirmText(options.confirmText || 'Confirm')
    setCancelText(options.cancelText || 'Cancel')
    setOnConfirmCallback(() => options.onConfirm)
    setIsOpen(true)
  }

  const closeModal = () => {
    setIsOpen(false)
    setIsLoading(false)
    setTitle('')
    setMessage('')
    setConfirmText('Confirm')
    setCancelText('Cancel')
    setOnConfirmCallback(undefined)
  }

  const handleConfirm = async () => {
    if (onConfirmCallback) {
      setIsLoading(true)
      try {
        await onConfirmCallback()
        closeModal()
      } catch (error) {
        setIsLoading(false)
        throw error
      }
    } else {
      closeModal()
    }
  }

  return (
    <ConfirmationModalContext.Provider
      value={{
        isOpen,
        closeModal,
        openModal,
        title,
        message,
        confirmText,
        cancelText,
        isLoading,
        onConfirm: handleConfirm,
      }}
    >
      {children}
      <ConfirmationModal />
    </ConfirmationModalContext.Provider>
  )
}

export const useConfirmationModal = () => {
  const context = React.useContext(ConfirmationModalContext)

  if (context === undefined) {
    throw new Error('useConfirmationModal must be used within a ConfirmationModalProvider')
  }

  return context
}
